<template>
  <div ref="container" @click.prevent="click">
    <slot ref="gallery" />
    <!-- Root element of PhotoSwipe. Must have class pswp. -->
    <div ref="template" class="pswp" tabindex="-1" role="dialog" aria-hidden="true">
      <!-- Background of PhotoSwipe.
           It's a separate element as animating opacity is faster than rgba().-->
      <div class="pswp__bg" />

      <!-- Slides wrapper with overflow:hidden. -->
      <div class="pswp__scroll-wrap">
        <!-- Container that holds slides.
             PhotoSwipe keeps only 3 of them in the DOM to save memory.
             Don't modify these 3 pswp__item elements, data is added later on.-->
        <div class="pswp__container">
          <div class="pswp__item" />
          <div class="pswp__item" />
          <div class="pswp__item" />
        </div>

        <!-- Default (PhotoSwipeUI_Default) interface on top of sliding area. Can be changed. -->
        <div class="pswp__ui pswp__ui--hidden">
          <div class="pswp__top-bar">
            <!--  Controls are self-explanatory. Order can be changed. -->

            <div class="pswp__counter" />

            <button class="rounded-none pswp__button pswp__button--close" title="Close (Esc)" />
            <button class="rounded-none pswp__button pswp__button--share" title="Share" />
            <button class="rounded-none pswp__button pswp__button--fs" title="Toggle fullscreen" />
            <button class="rounded-none pswp__button pswp__button--zoom" title="Zoom in/out" />

            <!-- Preloader demo https://codepen.io/dimsemenov/pen/yyBWoR -->
            <!-- element will get class pswp__preloader--active when preloader is running -->
            <div class="pswp__preloader">
              <div class="pswp__preloader__icn">
                <div class="pswp__preloader__cut">
                  <div class="pswp__preloader__donut" />
                </div>
              </div>
            </div>
          </div>

          <div class="pswp__share-modal pswp__share-modal--hidden pswp__single-tap">
            <div class="pswp__share-tooltip" />
          </div>

          <button class="pswp__button pswp__button--arrow--left" title="Previous (arrow left)" />
          <button class="pswp__button pswp__button--arrow--right" title="Next (arrow right)" />

          <div class="transform -translate-y-full pswp__caption">
            <div class="text-center pswp__caption__center" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PhotoSwipe from 'photoswipe'
import PhotoSwipeUI from 'photoswipe/src/js/ui/photoswipe-ui-default.js'
import ResizeSensor from 'css-element-queries/src/ResizeSensor'

export default {
  name: 'PhotoSwipe',
  props: {
    gid: {
      type: Number,
      required: false,
      default: 1,
    },
    fadeTransition: {
      type: Boolean,
      required: false,
      default: false,
    },
    esc: {
      type: Boolean,
      required: false,
      default: true,
    },
    history: {
      type: Boolean,
      required: false,
      default: false,
    },
    animationDuration: {
      type: Number,
      required: false,
      default: 333,
    },
    modal: {
      type: Boolean,
      required: false,
      default: true,
    },
    closeOnScroll: {
      type: Boolean,
      required: false,
      default: true,
    },
    zoom: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data () {
    return {
      items: [],
      photoswipe: null,
    }
  },
  computed: {
    options () {
      return this.photoswipe ? this.photoswipe.options : {}
    },
  },
  watch: {
    '$route' (to, from) {
      this.items = []
      this.photoswipe = null
      this.$nextTick(() => {
        this.setupElements()
      })
    },
  },
  mounted () {
    this.setupElements()

    const element = document.querySelector('.pswp')

    if (element) {
      // eslint-disable-next-line no-new
      new ResizeSensor(element, () => {
        if (this.photoswipe) this.photoswipe.updateSize(true)
      })
    }
  },
  methods: {
    setupElements () {
      const links = this.$el.querySelectorAll('.gallery-item')

      links.forEach((link, i) => {
        const image = link.querySelector('img')
        const item = {
        // Default single image
          src: link.getAttribute('href'),
          w: link.dataset.width,
          h: link.dataset.height,

          // Thumbnail image source
          msrc: this.zoom ? image.getAttribute('src') : false,
          el: link,

          // Optional
          title: link.getAttribute('title'),
        }

        link.dataset.pid = i

        this.items.push(item)
      })
    },
    click (e) {
      if (!this.photoswipe) {
        const el = e.target.closest('.gallery-item')

        if (el) {
          this.open(el.dataset.pid)
        }
      }
    },
    // Open photoswipe viewer
    open (pid, options) {
      this.photoswipe = new PhotoSwipe(
        this.$refs.template,
        PhotoSwipeUI,
        this.items,
        this.getOptions(pid, options),
      )

      this.photoswipe.init()

      this.photoswipe.listen('destroy', () => {
        this.photoswipe = null
      })
    },
    getOptions (pid, options = {}) {
      // See Options -> getThumbBoundsFn section of documentation for more info
      const getThumbBoundsFn = i => {
        if (this.zoom) {
          const { el, w, h } = this.items[i]
          const { left, top, width } = el.getBoundingClientRect()

          const offsetY = window.pageYOffset || document.documentElement.scrollTop

          const ratio = width / w

          return {
            x: left,
            y: top + offsetY + ((el.offsetHeight - h * ratio) / 2),
            w: width,
          }
        }

        return false
      }

      return {
        galleryUID: parseInt(this.gid, 10),
        index: parseInt(pid, 10),
        showHideOpacity: this.fadeTransition,
        escKey: this.esc,
        history: this.history,
        modal: this.modal,
        closeOnScroll: this.closeOnScroll,
        getThumbBoundsFn,
        fullscreenEl: false,
        shareEl: false,
        captionEl: false,
        counterEl: false,
        ...options,
      }
    },
  },
}
</script>

<style src="photoswipe/dist/photoswipe.css"></style>
<style src="photoswipe/dist/default-skin/default-skin.css"></style>
<style lang="postcss">
.pswp__bg {
  background-color: white;
}

.pswp__ui--fit .pswp__top-bar {
  background-color: transparent;
}

.pswp__top-bar, .pswp__caption {
  background-color: transparent;
}

.pswp__img--placeholder--blank {
    background: transparent;
}

.pswp__button,
.pswp__button--arrow--left:before,
.pswp__button--arrow--right:before {
  background: url("~@/assets/ui/default-skin.svg") 0 0 no-repeat;
  background-size: 264px 88px;
  background-color: rgba(255, 255, 255, 0.75);
}

@media (-webkit-min-device-pixel-ratio: 1.1),
  (-webkit-min-device-pixel-ratio: 1.09375),
  (min-resolution: 105dpi),
  (min-resolution: 1.1dppx) {
  /* Serve SVG sprite if browser supports SVG and resolution is more than 105dpi */
  .pswp--svg .pswp__button,
  .pswp--svg .pswp__button--arrow--left:before,
  .pswp--svg .pswp__button--arrow--right:before {
    background-image: url("~@/assets/ui/default-skin.svg");
  }

  .pswp--svg .pswp__button--arrow--left,
  .pswp--svg .pswp__button--arrow--right {
    background: none;
  }
}

.pswp--fs .pswp__button--fs {
  background-position: -44px 0;
}

.pswp__button--zoom {
  display: none;
  background-position: -88px 0;
}

.pswp__button--close {
  background-position: 0 -44px;
}

.pswp__button--share {
  background-position: -44px -44px;
}

.pswp__button--arrow--left,
.pswp__button--arrow--right {
  background: none;
}

.pswp__button--arrow--left:before {
  background-position: -138px -44px;
}

.pswp__button--arrow--right:before {
  background-position: -94px -44px;
}

.pswp__ui--fit .pswp__top-bar, .pswp__ui--fit .pswp__caption {
  background-color: transparent;
}

.pswp__caption__center {
  color: #ffffff;
}
</style>
